import {
   ChangeDetectorRef,
   Component,
   EventEmitter,
   Input,
   OnChanges,
   Output,
   SimpleChanges
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Property } from '../../domain/property';
import { PropertyStoreService } from '../../services/stores/property-store.service';
import { GroupService } from '../../services/group.service';
import { GroupsDropdown } from '../../domain/data/groups-dropdown';
import { RefreshDataState } from '../../domain/refresh-data-state';
import { DataStoreService } from '../../services/data-store.service';
import { MessageService, SelectItem, SelectItemGroup } from 'primeng/api';
import { Group } from '../../domain/group';

@Component({
   selector: 'app-add-group-dialog',
   templateUrl: './add-group-dialog.component.html',
   styleUrls: ['./add-group-dialog.component.scss'],
   providers: [GroupsDropdown, MessageService]
})
export class AddGroupDialogComponent implements OnChanges {
   @Input()
   isVisible: boolean;

   @Input()
   currentGroups: Group[];

   @Output() addGroupsDialogClose = new EventEmitter();

   groupForm: FormGroup;
   currentProperty: Property | undefined;
   selectedGroup: string | undefined;
   groupedGroupNames: SelectItemGroup[];

   constructor(
      private cdr: ChangeDetectorRef,
      private propertyStore: PropertyStoreService,
      private groupService: GroupService,
      private groupsDropdown: GroupsDropdown,
      private dataStoreService: DataStoreService,
      private messageService: MessageService
   ) {
      this.currentGroups = [];
      this.isVisible = false;
      this.groupForm = new FormGroup({
         group: new FormControl([], [Validators.required, Validators.minLength(1)])
      });
      this.currentProperty = undefined;
      this.propertyStore.currentProperty.subscribe((prop) => {
         this.currentProperty = prop;
      });
      this.groupedGroupNames = [];
   }

   get group() {
      return this.groupForm.get('group')!;
   }

   public close($event?: Event) {
      if ($event) $event.preventDefault();
      this.isVisible = false;
      this.groupForm.reset();
      this.addGroupsDialogClose.emit();
      this.cdr.detectChanges(); // Ensure UI updates after closing
   }

   public async onAddGroup($event: MouseEvent) {
      $event.preventDefault();
      if (this.currentProperty && this.group.valid) {
         this.groupService
            .addGroup(this.currentProperty.id, this.group.value)
            .subscribe({
               next: () => {
                  const refresh = new RefreshDataState();
                  refresh.Groups = true;
                  this.dataStoreService.refreshTaskData(refresh);
                  this.close();
                  this.messageService.add({
                     severity: 'success',
                     summary: 'Group Added',
                     detail: 'The group has been added successfully!',
                     life: 3000
                  });
               },
               error: (error) => {
                  this.messageService.add({
                     severity: 'error',
                     summary: 'Error Adding Group',
                     detail: 'There was an issue adding the group.',
                     life: 5000
                  });
                  if (error.status === 500) {
                     const refresh = new RefreshDataState();
                     refresh.Groups = true;
                     this.dataStoreService.refreshTaskData(refresh);
                     this.close();
                  }
               }
            });
      }
   }

   ngOnChanges(changes: SimpleChanges): void {
      if (changes['isVisible']?.currentValue === true && this.currentProperty?.id) {
         this.groupService.getGroups(this.currentProperty.id).subscribe((groups) => {
            this.currentGroups = groups ?? [];
            this.buildAvailableGroupOptions();
            this.cdr.detectChanges();
         });
      }

      if (changes['currentGroups']) {
         this.buildAvailableGroupOptions();
      }
   }

   private toTitleLabel(raw: string): string {
      // Replace underscores with spaces, then title-case each word and hyphen-part
      return raw
         .replace(/_/g, ' ')
         .split(' ')
         .map((word) =>
            word
               .split('-')
               .map((part) =>
                  part ? part[0].toUpperCase() + part.slice(1).toLowerCase() : part
               )
               .join('-')
         )
         .join(' ');
   }

   private buildAvailableGroupOptions() {
      const existing = new Set(
         (this.currentGroups ?? []).map((g) => (g.displayName ?? '').toLowerCase())
      );

      const groups = (this.groupsDropdown?.data ?? [])
         .map((group) => {
            const items: SelectItem[] = (group.groupNames ?? [])
               .filter((name) => !existing.has((name ?? '').toLowerCase()))
               .map((name) => ({ value: name, label: name }));

            if (!items.length) return null; // Skip empty groups

            const label = this.toTitleLabel(group.name ?? '');
            return { label, value: label, items } as SelectItemGroup;
         })
         .filter((g): g is SelectItemGroup => g !== null);

      this.groupedGroupNames = groups;
   }
}
